/**
 * The ground a road is built on.
 *
 * A road is not draped over the ground as a terrain model shows it: it is
 * graded, along a smooth profile and flat across, on an embankment where the
 * ground falls away and in a cutting where it rises. A coarse model carries
 * none of that. Interpolated across a carriageway, a 15 m grid (common round a
 * site) tilts it sideways and ripples it along, and every vehicle on it, and
 * the road painted on the ground, ripple with it.
 *
 * A road bed puts the road's own surface back. Inside the graded width the
 * ground is the road's profile, the ground's own height averaged across the
 * road and smoothed along it. Beyond that width it eases into the natural
 * ground over the side slope. Where two beds meet, at a junction, their
 * heights are averaged by how far inside each one the point lies.
 *
 * Runtime only: the 3D scene builds the beds from the roads it shows and
 * carries them on a copy of the terrain model. They are never saved with the
 * project.
 */

/** One graded road: its centreline, the surface's height at each vertex, its widths. */
export interface RoadBed {
  lat: number[];
  lng: number[];
  /** Height of the road surface at each vertex, absolute metres. */
  z: number[];
  /** Half the graded width: the paved width and its verge. */
  halfM: number;
  /** Width of the side slope from the graded edge out to the natural ground. */
  slopeM: number;
  /**
   * Whether the bed runs on past its first and last vertex, rounded, into
   * whatever meets it there (a junction). False ends square across the road:
   * at a bridge abutment, where the road leaves the ground, and where the
   * known ground stops.
   */
  capStart: boolean;
  capEnd: boolean;
}

/** The beds of one set of roads. `key` names the roads they were built from. */
export interface RoadBedSet {
  beds: RoadBed[];
  key: string;
}

/** What a bed is built from. */
export interface RoadBedInput {
  points: { lat: number; lng: number }[];
  halfM: number;
  slopeM: number;
  /** Spread of the smoothing along the road (Gaussian sigma), metres. */
  smoothM: number;
  /**
   * The road leaves the ground at this end, onto a bridge. Its bed ends
   * square there, and the ground within `abutmentM` of that end is not
   * sampled: an elevation grid coarser than the abutment blends the drop
   * under the deck into it, and the road would sag into the gap.
   */
  startAtBridge?: boolean;
  endAtBridge?: boolean;
}

/** How a set of beds is built. */
export interface RoadBedOptions {
  /** Ground not sampled next to a bridge, metres: about a cell of the elevation grid. */
  abutmentM?: number;
}

const M_PER_DEG = 111_320;
/** Vertices no further apart than this along a bed, metres. */
const STEP_M = 5;
/** Grid cell of the lookup index, degrees of latitude (about 55 m). */
const CELL_DEG = 0.0005;

interface Index {
  cells: Map<string, Array<{ bed: number; i: number }>>;
}
const indexes = new WeakMap<RoadBedSet, Index>();

const cellKey = (ix: number, iy: number) => `${ix},${iy}`;

function indexOf(set: RoadBedSet): Index {
  const cached = indexes.get(set);
  if (cached) return cached;
  const cells = new Map<string, Array<{ bed: number; i: number }>>();
  set.beds.forEach((b, bi) => {
    const reach = b.halfM + b.slopeM;
    for (let i = 0; i + 1 < b.lat.length; i++) {
      const latPad = reach / M_PER_DEG;
      const lngPad = reach / (M_PER_DEG * Math.max(0.05, Math.cos((b.lat[i] * Math.PI) / 180)));
      const x0 = Math.floor((Math.min(b.lng[i], b.lng[i + 1]) - lngPad) / CELL_DEG);
      const x1 = Math.floor((Math.max(b.lng[i], b.lng[i + 1]) + lngPad) / CELL_DEG);
      const y0 = Math.floor((Math.min(b.lat[i], b.lat[i + 1]) - latPad) / CELL_DEG);
      const y1 = Math.floor((Math.max(b.lat[i], b.lat[i + 1]) + latPad) / CELL_DEG);
      for (let ix = x0; ix <= x1; ix++) {
        for (let iy = y0; iy <= y1; iy++) {
          const k = cellKey(ix, iy);
          const list = cells.get(k);
          if (list) list.push({ bed: bi, i }); else cells.set(k, [{ bed: bi, i }]);
        }
      }
    }
  });
  const index = { cells };
  indexes.set(set, index);
  return index;
}

/** Whether a point lies past vertex `end` of a bed, going on from vertex `from`. */
function beyond(b: RoadBed, end: number, from: number, lat: number, lng: number, mPerLng: number): boolean {
  const ex = (b.lng[end] - b.lng[from]) * mPerLng;
  const ey = (b.lat[end] - b.lat[from]) * M_PER_DEG;
  return (lng - b.lng[end]) * mPerLng * ex + (lat - b.lat[end]) * M_PER_DEG * ey > 0;
}

/** Smoothstep from 0 to 1. */
const ease = (t: number) => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));

/**
 * The height the beds give a point, and how fully (1 on a road's graded
 * width, falling to 0 across its side slope), or null where no bed reaches.
 */
export function roadBedAt(set: RoadBedSet, lat: number, lng: number): { z: number; w: number } | null {
  const list = indexOf(set).cells.get(cellKey(Math.floor(lng / CELL_DEG), Math.floor(lat / CELL_DEG)));
  if (!list) return null;
  const mPerLng = M_PER_DEG * Math.cos((lat * Math.PI) / 180);
  let wSum = 0;
  let zSum = 0;
  let wMax = 0;
  for (const { bed, i } of list) {
    const b = set.beds[bed];
    const ax = (b.lng[i] - lng) * mPerLng;
    const ay = (b.lat[i] - lat) * M_PER_DEG;
    const bx = (b.lng[i + 1] - lng) * mPerLng;
    const by = (b.lat[i + 1] - lat) * M_PER_DEG;
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 > 0 ? Math.min(1, Math.max(0, -(ax * dx + ay * dy) / len2)) : 0;
    // Past a square end, the bed is not there at all, whichever of its segments is nearest.
    if (!b.capStart && beyond(b, 0, 1, lat, lng, mPerLng)) continue;
    if (!b.capEnd && beyond(b, b.lat.length - 1, b.lat.length - 2, lat, lng, mPerLng)) continue;
    const d = Math.hypot(ax + dx * t, ay + dy * t);
    if (d >= b.halfM + b.slopeM) continue;
    const w = d <= b.halfM ? 1 : ease(1 - (d - b.halfM) / b.slopeM);
    if (!(w > 0)) continue;
    const z = b.z[i] + (b.z[i + 1] - b.z[i]) * t;
    wSum += w;
    zSum += w * z;
    if (w > wMax) wMax = w;
  }
  return wSum > 0 ? { z: zSum / wSum, w: wMax } : null;
}

/** A polyline with no two vertices further apart than STEP_M. */
function densify(points: { lat: number; lng: number }[]): { lat: number; lng: number }[] {
  const out: { lat: number; lng: number }[] = [points[0]];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const mPerLng = M_PER_DEG * Math.cos((a.lat * Math.PI) / 180);
    const len = Math.hypot((b.lng - a.lng) * mPerLng, (b.lat - a.lat) * M_PER_DEG);
    const n = Math.max(1, Math.ceil(len / STEP_M));
    for (let k = 1; k <= n; k++) out.push({ lat: a.lat + ((b.lat - a.lat) * k) / n, lng: a.lng + ((b.lng - a.lng) * k) / n });
  }
  return out;
}

/**
 * Build the beds for a set of roads from the natural ground (`ground`, the
 * terrain model WITHOUT beds). Each road's profile is the ground across its
 * graded width (centre and both edges) at every vertex, smoothed along it.
 *
 * `ground` returns NaN where the ground is not known, past the elevation data.
 * A bed is not graded from a stand-in height there: it stops where the known
 * ground stops, and only a gap shorter than its smoothing is bridged.
 */
export function buildRoadBeds(
  inputs: readonly RoadBedInput[],
  ground: (lat: number, lng: number) => number,
  key: string,
  options: RoadBedOptions = {},
): RoadBedSet {
  const abutmentM = options.abutmentM ?? 15;
  const beds: RoadBed[] = [];
  for (const input of inputs) {
    if (input.points.length < 2) continue;
    const pts = densify(input.points);
    const n = pts.length;
    const s = new Array<number>(n).fill(0);
    for (let i = 1; i < n; i++) {
      const p = pts[i];
      const o = pts[i - 1];
      const mPerLng = M_PER_DEG * Math.cos((p.lat * Math.PI) / 180);
      s[i] = s[i - 1] + Math.hypot((p.lng - o.lng) * mPerLng, (p.lat - o.lat) * M_PER_DEG);
    }
    const total = s[n - 1];
    // Next to an abutment: no sample, but still the road's bed.
    const abutment = (i: number) => (input.startAtBridge === true && s[i] < abutmentM)
      || (input.endAtBridge === true && total - s[i] < abutmentM);
    const raw = new Array<number>(n);
    for (let i = 0; i < n; i++) {
      const p = pts[i];
      const q = pts[Math.min(n - 1, i + 1)];
      const o = pts[Math.max(0, i - 1)];
      const mPerLng = M_PER_DEG * Math.cos((p.lat * Math.PI) / 180);
      if (abutment(i)) { raw[i] = NaN; continue; }
      // Across the road: the left normal of the local direction.
      const ex = (q.lng - o.lng) * mPerLng;
      const ny = (q.lat - o.lat) * M_PER_DEG;
      const len = Math.hypot(ex, ny) || 1;
      const lx = (-ny / len) * input.halfM;
      const ly = (ex / len) * input.halfM;
      const at = (sx: number, sy: number) => ground(p.lat + sy / M_PER_DEG, p.lng + sx / mPerLng);
      const samples = [ground(p.lat, p.lng), at(lx, ly), at(-lx, -ly)].filter((z) => Number.isFinite(z));
      raw[i] = samples.length > 0 ? samples.reduce((a, b) => a + b, 0) / samples.length : NaN;
    }
    const sigma = Math.max(1, input.smoothM);
    const z = new Array<number>(n);
    for (let i = 0; i < n; i++) {
      let wSum = 0;
      let zSum = 0;
      for (let k = i; k >= 0 && s[i] - s[k] <= 3 * sigma; k--) {
        if (!Number.isFinite(raw[k])) continue;
        const w = Math.exp(-0.5 * ((s[i] - s[k]) / sigma) ** 2);
        wSum += w; zSum += w * raw[k];
      }
      for (let k = i + 1; k < n && s[k] - s[i] <= 3 * sigma; k++) {
        if (!Number.isFinite(raw[k])) continue;
        const w = Math.exp(-0.5 * ((s[k] - s[i]) / sigma) ** 2);
        wSum += w; zSum += w * raw[k];
      }
      z[i] = wSum > 0 ? zSum / wSum : raw[i];
    }
    const known = raw.map((v, i) => Number.isFinite(v) || (abutment(i) && Number.isFinite(z[i])));
    for (let i = 0; i < n; i++) {
      if (known[i]) continue;
      let j = i;
      while (j < n && !known[j]) j++;
      if (i > 0 && j < n && s[j] - s[i - 1] <= sigma) for (let k = i; k < j; k++) known[k] = Number.isFinite(z[k]);
      i = j;
    }
    for (let i = 0; i < n; i++) {
      if (!known[i]) continue;
      let j = i;
      while (j < n && known[j]) j++;
      if (j - i >= 2) {
        beds.push({
          lat: pts.slice(i, j).map((p) => p.lat),
          lng: pts.slice(i, j).map((p) => p.lng),
          z: z.slice(i, j),
          halfM: input.halfM,
          slopeM: Math.max(0.5, input.slopeM),
          // Rounded only where the road really runs on into a junction.
          capStart: i === 0 && input.startAtBridge !== true,
          capEnd: j === n && input.endAtBridge !== true,
        });
      }
      i = j;
    }
  }
  return { beds, key };
}
